import { defineStore } from "pinia";
import { ProjectsApi, TrainingPlanApi } from "@simbelapp/order-sdk";
import type { ItemType as SdkItemType } from "@simbelapp/order-sdk";
import { TrainingPlanTypesEnum } from "~/utils/enums/training-plan-enums";
import type { ITrainingPlanStore } from "~/utils/interfaces/training-plans-interface";
import { usePagination } from "~/composables/paginations/usePagination";
import { useFeedback } from "~/composables/feedback/useFeedback";
import { useSessionDetailsStore } from "~/store/sessions/session-details-store";
import { ItemType, MicroserviceEnum } from "~/utils/enums/common.enums";
import { useSDKApi } from "~/composables/api/useSDKApi";
import { useFilters } from "~/composables/filters/useFilters";

export const useTrainingsPlansStore = defineStore("TrainingsPlansStore", {
  state: (): ITrainingPlanStore => {
    return {
      trainingsPlans: null,
      myTrainingPlans: null,
      mySessions: null,
      mySessionsCounts: null,
      offset: 0,
      maxOffset: 0,
      fetchLoading: false,
      showEditTagModal: false,
      showDuplicateSessionModal: false,
      duplicateItem: {
        itemId: null,
        itemType: null,
        startDate: null,
        startTime: null,
        keepTrainers: false,
        keepLocations: false,
      },
      categoryTag: null,
      showVisibilitySessionModal: false,
    };
  },
  getters: {},
  actions: {
    resetPaginate() {
      this.mySessions = null;
      this.offset = 0;
      this.maxOffset = 0;
    },
    async fetchTrainingsPlansIndividual(query: { itemType: string }) {
      const pagination = usePagination();
      const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, TrainingPlanApi);
      const feedback = useFeedback();
      const filters = useFilters();

      const paginateOptions = pagination.getFindOptions(pagination.paginateOptions.value);

      if (!pagination.paginateOptions.value.sortingColumn) {
        paginateOptions.sortingColumn = "start_date";
      }

      if (!pagination.paginateOptions.value.order) {
        paginateOptions.order = "ASC";
      }

      const paginateQuery = new URLSearchParams(paginateOptions);
      const queryString = Object.fromEntries(paginateQuery.entries());
      const filtersQuery = filters.getFiltersForSDK();

      const response = await apiInstance.getAllTrainingPlans({
        ...queryString,
        sortingColumn: paginateOptions.sortingColumn,
        ...filtersQuery,
        ...query,
      });
      if (response) {
        this.trainingsPlans = response;
      } else {
        feedback.error(`erreur`, "small");
      }
      return response;
    },
    async fetchTrainingsPlansIndividualData(query: { itemType: string }) {
      const pagination = usePagination();
      const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, TrainingPlanApi);
      const feedback = useFeedback();
      const filters = useFilters();

      const paginateOptions = pagination.getFindOptions(pagination.paginateOptions.value);

      if (!pagination.paginateOptions.value.sortingColumn) {
        paginateOptions.sortingColumn = "start_date";
      }

      if (!pagination.paginateOptions.value.order) {
        paginateOptions.order = "ASC";
      }

      const paginateQuery = new URLSearchParams(paginateOptions);
      const queryString = Object.fromEntries(paginateQuery.entries());
      const filtersQuery = filters.getFiltersForSDK();

      const response = await apiInstance.getAllTrainingPlansData({
        ...queryString,
        sortingColumn: paginateOptions.sortingColumn,
        ...filtersQuery,
        ...query,
      });
      if (response.data) {
        this.trainingsPlans!.data = response.data;
      } else {
        feedback.error(`erreur`, "small");
      }
      return response;
    },
    async fetchMyTrainingsPlans(itemType: TrainingPlanTypesEnum): Promise<void> {
      const pagination = usePagination();
      const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, TrainingPlanApi);
      const feedback = useFeedback();
      this.fetchLoading = true;

      const paginateOptions = pagination.getFindOptions(pagination.paginateOptions.value);

      if (!pagination.paginateOptions.value.sortingColumn) {
        paginateOptions.sortingColumn = "start_date";
      }

      if (!pagination.paginateOptions.value.order) {
        paginateOptions.order = "ASC";
      }

      delete paginateOptions.filters;

      const paginateQuery = new URLSearchParams(paginateOptions);
      const queryString = Object.fromEntries(paginateQuery.entries());

      try {
        const response = await apiInstance.getMyTrainingPlans({
          ...queryString,
          sortingColumn: paginateOptions.sortingColumn,
          itemType,
        });

        if (response) {
          if (this.mySessions === null) this.mySessions = [];
          this.mySessions.push(...response.data);
          this.offset += response.data.length;
          this.mySessionsCounts = response.meta.totalCount;
          pagination.paginateOptions.value.offset = this.offset;
          this.myTrainingPlans = response;
        }
      } catch {
        feedback.error("Une erreur est survenue", "small");
      } finally {
        this.fetchLoading = false;
      }
    },
    async fetchTrainingsPlansSessions(query: { itemType: string }) {
      const pagination = usePagination();
      const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, TrainingPlanApi);
      const feedback = useFeedback();
      const filters = useFilters();

      const paginateOptions = pagination.getFindOptions(pagination.paginateOptions.value);

      if (!pagination.paginateOptions.value.sortingColumn) {
        paginateOptions.sortingColumn = "start_date";
      }

      if (!pagination.paginateOptions.value.order) {
        paginateOptions.order = "ASC";
      }

      const paginateQuery = new URLSearchParams(paginateOptions);
      const queryString = Object.fromEntries(paginateQuery.entries());
      const filtersQuery = filters.getFiltersForSDK();

      const response = await apiInstance.getTrainingPlanSessions({
        ...queryString,
        sortingColumn: paginateOptions.sortingColumn,
        ...filtersQuery,
        ...query,
      });
      if (response) {
        this.myTrainingPlans = response;
      } else {
        feedback.error(`erreur`, "small");
      }
      return response;
    },
    async fetchTrainingsPlansSessionsData(query: { itemType: string }) {
      const pagination = usePagination();
      const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, TrainingPlanApi);
      const feedback = useFeedback();
      const filters = useFilters();

      const paginateOptions = pagination.getFindOptions(pagination.paginateOptions.value);

      if (!pagination.paginateOptions.value.sortingColumn) {
        paginateOptions.sortingColumn = "start_date";
      }

      if (!pagination.paginateOptions.value.order) {
        paginateOptions.order = "ASC";
      }

      const paginateQuery = new URLSearchParams(paginateOptions);
      const queryString = Object.fromEntries(paginateQuery.entries());
      const filtersQuery = filters.getFiltersForSDK();

      const response = await apiInstance.getTrainingPlanSessionsData({
        ...queryString,
        sortingColumn: paginateOptions.sortingColumn,
        ...filtersQuery,
        ...query,
      });
      if (response.data) {
        this.myTrainingPlans!.data = response.data;
      } else {
        feedback.error(`erreur`, "small");
      }
      return response;
    },

    async duplicateSession(): Promise<void> {
      const feedback = useFeedback();
      const sessionDetailsStore = useSessionDetailsStore();

      if (this.duplicateItem.itemId && this.duplicateItem.itemType) {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, ProjectsApi);

        const data = await apiInstance.duplicateItem({
          duplicateItemDto: {
            itemId: this.duplicateItem.itemId,
            itemType: this.duplicateItem.itemType,
            startDate: this.duplicateItem.startDate ? this.duplicateItem.startDate : undefined,
            keepTrainers: this.duplicateItem.keepTrainers,
            keepLocations: this.duplicateItem.keepLocations,
          },
        });

        const newItemId = data.newItemId;
        if (newItemId) {
          const { sessionViewModal } = storeToRefs(sessionDetailsStore);
          sessionViewModal.value = false;
          feedback.success("Nouveau brouillon dupliqué.", "small");

          sessionDetailsStore.showMySession(newItemId, ItemType.PROJECT);
        }
      } else {
        feedback.success("Session dupliquée avec succès", "small");
      }
    },
    async batchAssignTpGroups(
      itemIds: string[],
      selectAll: boolean,
      itemType: SdkItemType,
      groupsAssigned: any[],
      transformQuery?: any,
    ): Promise<void> {
      const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, TrainingPlanApi);
      const feedback = useFeedback();

      try {
        await apiInstance.batchAssignTpGroups({
          batchAssignGroupsDto: {
            itemIds,
            groupIds: groupsAssigned.map((g) => g.groupId),
            selectAll,
            itemType,
          },
          ...transformQuery,
        });
      } catch {
        feedback.error(`erreur`, "small");
      }
    },
    // Triggers a CSV export of the individual training plan using the current filters and sorting (no pagination).
    async fetchTrainingPlanCsv(): Promise<void> {
      const pagination = usePagination();
      const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, TrainingPlanApi);
      const feedback = useFeedback();
      const filters = useFilters();

      const paginateOptions = pagination.getFindOptions(pagination.paginateOptions.value);

      delete paginateOptions.offset;
      delete paginateOptions.limit;

      const paginateQuery = new URLSearchParams(paginateOptions);
      const queryString = Object.fromEntries(paginateQuery.entries());
      const filtersQuery = filters.getFiltersForSDK();

      try {
        await apiInstance.exportTrainingPlanIndividualCSVLinkRaw({
          ...queryString,
          ...filtersQuery,
        });
      } catch {
        feedback.error(`Erreur lors de l'export CSV du plan de formation`, "small");
      }
    },
    // Triggers an XLSX export of the training plan using the current filters and sorting (no pagination).
    async fetchTrainingPlanXlsx(): Promise<void> {
      const pagination = usePagination();
      const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, TrainingPlanApi);
      const feedback = useFeedback();
      const filters = useFilters();

      const paginateOptions = pagination.getFindOptions(pagination.paginateOptions.value);

      delete paginateOptions.offset;
      delete paginateOptions.limit;

      const paginateQuery = new URLSearchParams(paginateOptions);
      const queryString = Object.fromEntries(paginateQuery.entries());
      const filtersQuery = filters.getFiltersForSDK();

      try {
        await apiInstance.exportTrainingPlanXLSXLinkRaw({
          ...queryString,
          ...filtersQuery,
        });
      } catch {
        feedback.error(`Erreur lors de l'export XLSX du plan de formation`, "small");
      }
    },
  },
});
